// Report Templates — manage named, versioned document layouts that drive
// generation (DARs, incidents, invoices, post orders, surveys, advances, CoC).
//
// Two panes: a left list of templates (filterable by kind) and a right detail
// pane for the selected template showing its versions. Drafts are editable;
// publishing promotes a version to 'active' (archiving the previous active one)
// and is gated separately on report_template.publish.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import type {
  ReportTemplate,
  ReportTemplateKind,
  ReportTemplateVersion,
  ReportTemplateVersionStatus,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Select,
  Table,
  Textarea,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import {
  useReportTemplates,
  useReportTemplateVersions,
  useCreateReportTemplate,
  useUpdateReportTemplate,
  useDeleteReportTemplate,
  usePublishReportTemplate,
  useCreateReportTemplateVersion,
  useUpdateReportTemplateVersion,
  useDeleteReportTemplateVersion,
  type ReportTemplateFilters,
  type VersionFilters,
} from '../api/queries/templates';

// ---------- Static option lists ----------

const KINDS: { value: ReportTemplateKind; label: string }[] = [
  { value: 'dar', label: 'Daily Activity Report' },
  { value: 'incident', label: 'Incident Report' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'post_order', label: 'Post Order' },
  { value: 'survey', label: 'Site Survey' },
  { value: 'advance', label: 'Advance' },
  { value: 'coc', label: 'Chain of Custody' },
];

const VERSION_STATUSES: ReportTemplateVersionStatus[] = ['draft', 'active', 'archived'];

function kindLabel(kind: ReportTemplateKind): string {
  return KINDS.find((k) => k.value === kind)?.label ?? kind;
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number): string {
  return new Date(epochMs).toLocaleString();
}

// =============================================================================
// Page
// =============================================================================

export function ReportTemplatesPage(): JSX.Element {
  const canWrite = can('report_template.write');
  const canPublish = can('report_template.publish');

  const [filters, setFilters] = useState<ReportTemplateFilters>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ReportTemplate | null>(null);
  const [deleting, setDeleting] = useState<ReportTemplate | null>(null);

  const templatesQuery = useReportTemplates(filters);
  const deleteTemplate = useDeleteReportTemplate();

  const templates = templatesQuery.data?.report_templates ?? [];
  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteTemplate.mutateAsync(deleting.id);
      toast.success(`Deleted template “${deleting.name}”.`);
      if (selectedId === deleting.id) setSelectedId(null);
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete template.'));
    }
  };

  const kindFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All kinds' },
    ...KINDS.map((k) => ({ value: k.value, label: k.label })),
  ];

  return (
    <div className="report-templates-page">
      <header className="page-header">
        <div>
          <h2>Report Templates</h2>
          <p className="page-sub">
            Versioned layouts used to generate documents across the platform.
          </p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New template
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={filters.kind ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              kind: v !== 'all' ? (v as ReportTemplateKind) : undefined,
            }))
          }
          options={kindFilterOptions}
          aria-label="Filter by kind"
        />
      </div>

      <div className="split-layout">
        <section className="split-list" aria-label="Templates">
          {templatesQuery.isLoading && <p className="muted">Loading templates…</p>}
          {templatesQuery.isError && (
            <p className="error-text" role="alert">
              {errMessage(templatesQuery.error, 'Failed to load templates.')}
            </p>
          )}
          {!templatesQuery.isLoading && templates.length === 0 && (
            <p className="muted empty-state">No report templates match these filters.</p>
          )}

          <ul className="list-cards">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`list-card ${selectedId === t.id ? 'active' : ''}`}
                  onClick={() => setSelectedId(t.id)}
                  aria-pressed={selectedId === t.id}
                >
                  <div className="list-card-main">
                    <span className="list-card-title">{t.name}</span>
                    <Badge tone="neutral">{kindLabel(t.kind)}</Badge>
                  </div>
                  <div className="list-card-meta">
                    {t.active_version_id !== null ? (
                      <Badge tone="success">Published</Badge>
                    ) : (
                      <Badge tone="neutral">No active version</Badge>
                    )}
                    {t.description && (
                      <span className="list-card-desc">{t.description}</span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="split-detail" aria-label="Template detail">
          {!selected ? (
            <div className="detail-placeholder muted">
              Select a template to view and manage its versions.
            </div>
          ) : (
            <TemplateDetail
              key={selected.id}
              template={selected}
              canWrite={canWrite}
              canPublish={canPublish}
              onEdit={() => setEditing(selected)}
              onDelete={() => setDeleting(selected)}
            />
          )}
        </section>
      </div>

      {/* Create template */}
      {createOpen && (
        <TemplateFormModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onCreated={(t) => {
            setCreateOpen(false);
            setSelectedId(t.id);
          }}
        />
      )}

      {/* Edit template metadata */}
      {editing && (
        <TemplateFormModal
          mode="edit"
          template={editing}
          onClose={() => setEditing(null)}
          onCreated={() => setEditing(null)}
        />
      )}

      {/* Delete template */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete template"
        message={
          deleting
            ? `Delete “${deleting.name}” and all its versions? This can be restored only by an administrator.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteTemplate.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// =============================================================================
// Template create/edit modal
// =============================================================================

interface TemplateFormModalProps {
  mode: 'create' | 'edit';
  template?: ReportTemplate;
  onClose: () => void;
  onCreated: (t: ReportTemplate) => void;
}

function TemplateFormModal({
  mode,
  template,
  onClose,
  onCreated,
}: TemplateFormModalProps): JSX.Element {
  const create = useCreateReportTemplate();
  const update = useUpdateReportTemplate();
  const busy = create.isPending || update.isPending;

  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [kind, setKind] = useState<ReportTemplateKind>(template?.kind ?? 'dar');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.warn('Name is required.');
      return;
    }
    const desc = description.trim();
    try {
      if (mode === 'create') {
        const res = await create.mutateAsync({
          name: trimmedName,
          kind,
          ...(desc ? { description: desc } : {}),
        });
        toast.success(`Created template “${res.report_template.name}”.`);
        onCreated(res.report_template);
      } else if (template) {
        const res = await update.mutateAsync({
          id: template.id,
          name: trimmedName,
          kind,
          description: desc ? desc : null,
        });
        toast.success('Template updated.');
        onCreated(res.report_template);
      }
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save template.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New report template' : 'Edit template'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="template-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="template-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Name" required>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            autoFocus
          />
        </FormField>

        <FormField label="Kind">
          <select
            className="form-input"
            value={kind}
            onChange={(e) => setKind(e.target.value as ReportTemplateKind)}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={10_000}
            placeholder="What this template is for (optional)."
          />
        </FormField>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Template detail — versions
// =============================================================================

interface TemplateDetailProps {
  template: ReportTemplate;
  canWrite: boolean;
  canPublish: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function TemplateDetail({
  template,
  canWrite,
  canPublish,
  onEdit,
  onDelete,
}: TemplateDetailProps): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<VersionFilters>({});
  const [versionModal, setVersionModal] = useState<
    { mode: 'create' } | { mode: 'edit'; version: ReportTemplateVersion } | null
  >(null);
  const [publishing, setPublishing] = useState<ReportTemplateVersion | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<ReportTemplateVersion | null>(
    null,
  );

  const versionsQuery = useReportTemplateVersions(template.id, statusFilter);
  const publish = usePublishReportTemplate();
  const deleteVersion = useDeleteReportTemplateVersion();

  const versions = versionsQuery.data?.report_template_versions ?? [];

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All' },
    ...VERSION_STATUSES.map((s) => ({ value: s, label: s })),
  ];

  const onConfirmPublish = async (): Promise<void> => {
    if (!publishing) return;
    try {
      await publish.mutateAsync({ templateId: template.id, version_id: publishing.id });
      toast.success(`Published version ${publishing.version}.`);
      setPublishing(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to publish version.'));
    }
  };

  const onConfirmDeleteVersion = async (): Promise<void> => {
    if (!deletingVersion) return;
    try {
      await deleteVersion.mutateAsync({
        templateId: template.id,
        versionId: deletingVersion.id,
      });
      toast.success(`Deleted version ${deletingVersion.version}.`);
      setDeletingVersion(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete version.'));
    }
  };

  return (
    <div className="template-detail">
      <header className="detail-header">
        <div>
          <h3>{template.name}</h3>
          <div className="detail-subline">
            <Badge tone="neutral">{kindLabel(template.kind)}</Badge>
            {template.active_version_id !== null ? (
              <Badge tone="success">
                Active version #{template.active_version_id}
              </Badge>
            ) : (
              <Badge tone="neutral">No active version</Badge>
            )}
          </div>
          {template.description && (
            <p className="detail-description">{template.description}</p>
          )}
        </div>
        {canWrite && (
          <div className="detail-actions">
            <Button variant="ghost" onClick={onEdit}>
              Edit
            </Button>
            <Button variant="danger" onClick={onDelete}>
              Delete
            </Button>
          </div>
        )}
      </header>

      <div className="detail-section">
        <div className="detail-section-head">
          <h4>Versions</h4>
          <div className="detail-section-tools">
            <Select
              value={statusFilter.status ?? 'all'}
              onValueChange={(v) =>
                setStatusFilter({
                  status:
                    v !== 'all' ? (v as ReportTemplateVersionStatus) : undefined,
                })
              }
              options={statusFilterOptions}
              aria-label="Filter versions by status"
            />
            {canWrite && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setVersionModal({ mode: 'create' })}
              >
                New version
              </button>
            )}
          </div>
        </div>

        {versionsQuery.isLoading && <p className="muted">Loading versions…</p>}
        {versionsQuery.isError && (
          <p className="error-text" role="alert">
            {errMessage(versionsQuery.error, 'Failed to load versions.')}
          </p>
        )}
        {!versionsQuery.isLoading && versions.length === 0 && (
          <p className="muted empty-state">
            No versions yet. Create a draft version, then publish it to make this
            template usable.
          </p>
        )}

        {versions.length > 0 && (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Notes</Table.HeaderCell>
                <Table.HeaderCell>Updated</Table.HeaderCell>
                <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {versions.map((v) => (
                <Table.Row key={v.id}>
                  <Table.Cell tone="mono">#{v.version}</Table.Cell>
                  <Table.Cell>
                    <VersionStatusBadge status={v.status} />
                  </Table.Cell>
                  <Table.Cell tone="muted" className="cell-truncate">
                    {v.notes ?? '—'}
                  </Table.Cell>
                  <Table.Cell>{fmtDate(v.updated_at)}</Table.Cell>
                  <Table.Cell className="col-actions">
                    {canWrite && v.status === 'draft' && (
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => setVersionModal({ mode: 'edit', version: v })}
                      >
                        Edit
                      </Button>
                    )}
                    {canPublish && v.status !== 'active' && (
                      <Button
                        variant="primary"
                        className="btn-sm"
                        onClick={() => setPublishing(v)}
                      >
                        Publish
                      </Button>
                    )}
                    {canWrite && (
                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => setDeletingVersion(v)}
                      >
                        Delete
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>

      {/* Create / edit version */}
      {versionModal && (
        <VersionFormModal
          templateId={template.id}
          version={versionModal.mode === 'edit' ? versionModal.version : undefined}
          onClose={() => setVersionModal(null)}
        />
      )}

      {/* Publish confirm */}
      <ConfirmDialog
        open={publishing !== null}
        title="Publish version"
        message={
          publishing
            ? `Make version ${publishing.version} the active version for “${template.name}”? Any currently active version will be archived.`
            : ''
        }
        confirmLabel="Publish"
        busy={publish.isPending}
        onConfirm={onConfirmPublish}
        onCancel={() => setPublishing(null)}
      />

      {/* Delete version confirm */}
      <ConfirmDialog
        open={deletingVersion !== null}
        title="Delete version"
        message={
          deletingVersion
            ? `Delete version ${deletingVersion.version}? If it is the active version the template will be unpublished.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteVersion.isPending}
        onConfirm={onConfirmDeleteVersion}
        onCancel={() => setDeletingVersion(null)}
      />
    </div>
  );
}

function VersionStatusBadge({
  status,
}: {
  status: ReportTemplateVersionStatus;
}): JSX.Element {
  const tone: BadgeTone =
    status === 'active' ? 'success' : status === 'draft' ? 'warn' : 'neutral';
  return <Badge tone={tone}>{status}</Badge>;
}

// =============================================================================
// Version create/edit modal
// =============================================================================

interface VersionFormModalProps {
  templateId: number;
  /** Present => edit a draft; absent => create the next draft version. */
  version?: ReportTemplateVersion;
  onClose: () => void;
}

function VersionFormModal({
  templateId,
  version,
  onClose,
}: VersionFormModalProps): JSX.Element {
  const isEdit = version !== undefined;
  const create = useCreateReportTemplateVersion();
  const update = useUpdateReportTemplateVersion();
  const busy = create.isPending || update.isPending;

  const [notes, setNotes] = useState(version?.notes ?? '');
  const [layoutText, setLayoutText] = useState(() =>
    version ? JSON.stringify(version.layout, null, 2) : '{}',
  );
  const [layoutError, setLayoutError] = useState<string | null>(null);

  const parseLayout = (): Record<string, unknown> | undefined => {
    const text = layoutText.trim();
    if (text === '') return {};
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setLayoutError('Layout must be a JSON object.');
        return undefined;
      }
      setLayoutError(null);
      return parsed as Record<string, unknown>;
    } catch {
      setLayoutError('Layout is not valid JSON.');
      return undefined;
    }
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const layout = parseLayout();
    if (layout === undefined) return;
    const trimmedNotes = notes.trim();
    try {
      if (isEdit && version) {
        await update.mutateAsync({
          templateId,
          versionId: version.id,
          layout,
          notes: trimmedNotes ? trimmedNotes : null,
        });
        toast.success(`Version ${version.version} updated.`);
      } else {
        const res = await create.mutateAsync({
          templateId,
          layout,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        });
        toast.success(`Created draft version ${res.report_template_version.version}.`);
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save version.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? `Edit version ${version?.version}` : 'New version'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="version-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Create draft'}
          </Button>
        </>
      }
    >
      <form id="version-form" onSubmit={onSubmit} className="form-grid">
        {!isEdit && (
          <p className="muted form-hint">
            A new draft version is created with the next sequential number. Publish
            it when ready to make it the active layout.
          </p>
        )}

        <FormField label="Layout (JSON)">
          <Textarea
            className={`code-input ${layoutError ? 'invalid' : ''}`}
            invalid={layoutError !== null}
            value={layoutText}
            onChange={(e) => setLayoutText(e.target.value)}
            rows={12}
            spellCheck={false}
            placeholder="{}"
          />
          {layoutError && (
            <span className="field-error" role="alert">
              {layoutError}
            </span>
          )}
        </FormField>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={10_000}
            placeholder="What changed in this version (optional)."
          />
        </FormField>
      </form>
    </Dialog>
  );
}
